/**
 * i18next initialisation for the StudioFlow GUI.
 *
 * NO locale is bundled. Every namespace, `common`/`layout`/`auth2` included, is
 * fetched on demand by LazyBackend as one chunk per locale × namespace. Until
 * 2026-09-10 four namespaces were statically imported for all six locales, which
 * is a 103 KB gzipped chunk, modulepreloaded at top priority, of which a session
 * uses one sixth. An English user now pays ~15 KB for English; a German user pays
 * the same and receives no English at all (IN-46 Phase 3).
 *
 * The detector resolves the language SYNCHRONOUSLY — saved preference in
 * localStorage, else navigator.language — so the core-namespace fetch is issued at
 * module evaluation, and Suspense holds the paint until it lands rather than
 * flashing raw keys.
 *
 * Language resolution on startup (highest priority wins):
 *   1. Explicit user preference (member.uiLocale from /me/preferences)
 *      — fetched and applied by useUiLocaleSync after auth.
 *   2. Browser detection (navigator.language) via LanguageDetector, normalised
 *      through `resolveLocale` by `convertDetectedLanguage` below.
 *   3. `lng: 'en-US'` when detection yields nothing at all.
 *
 * Shipped scope: en-US, de-CH, fr-CH, it-CH, es-ES, pt-BR, hu-HU — Switzerland's
 * three official languages, international English fallback, plus Spanish,
 * Brazilian Portuguese and Hungarian. hu-HU (RB-12 C2) ships as a copy of en-US
 * until its translations land (RB-12 C3). A further 14 LLM-translated locale snapshots used to
 * sit unreferenced under locales/_experimental/; they were deleted on
 * 2026-09-07 and are recoverable from git history (see that commit for the
 * reactivation procedure). Adding a locale means a content-quality pass, not
 * a file copy — see docs/archive/i18n-completion-runbook.md.
 */
import i18n, { type InitOptions } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import LazyBackend from './lazyBackend'
import { SUPPORTED_LOCALES, resolveLocale } from './supportedLocales'
import { syncHtmlLang } from './htmlLang'

// Every namespace, every locale, is loaded on demand — see lazyBackend.ts.

// The locale set and `resolveLocale` live in a side-effect-free module so callers
// that must not boot i18next (shared/lib/formatters.ts) can import them. Re-exported
// here so `from '@/i18n'` keeps working everywhere it is already used.
export {
  SUPPORTED_LOCALES,
  CLIENT_LOCALES,
  resolveLocale,
  type SupportedLocale,
  type ClientLocale,
} from './supportedLocales'

// Namespaces are split per-surface so parallel i18n work doesn't
// collide on a single mega-file. 'common' holds buttons, statuses,
// nav, and app-wide micro-strings — everything else lives in its
// own namespace.
//
// `ns` is what i18next preloads for the detected language before React renders.
//
// It lists all twelve on purpose, and shrinking it to the three core namespaces
// is NOT a safe edit today — that is runbook Phase 3b, and it is deferred.
// 166 source files read namespace-prefixed keys (`t('lists:…')`, `t('details:…')`)
// from a bare `useTranslation()`, which works only because every namespace is
// already loaded here. Cutting this list makes each of those render raw keys on
// any route reached cold. The test suite cannot warn you: src/test/setup.ts seeds
// every locale eagerly, so the failure appears on a phone, not in CI.
// `.claude/scripts/cross-namespace-t-calls.py` lists the 166.
//
// Phase 3a — the part that shipped — is the expensive half anyway: nothing is
// bundled and there is no en-US fallback fetch, which took the entry chunk from
// 236 KB to 177 KB gz and halved the locale requests for every non-English user.
// Preloading twelve namespaces for ONE language still beats eight-plus-eight for
// two. Re-measure after Phase 5.
const NAMESPACES = [
  'common', 'layout', 'dashboard', 'lists',
  'details', 'settings', 'workflow', 'auth2',
  'publicGallery', 'publicshare', 'booking',
  'documentDesign',
] as const

/**
 * The init options, exported so a test can boot a SEPARATE i18next instance with
 * exactly these options and assert real detector behaviour.
 *
 * i18next's default export is a process-wide singleton, and `src/test/setup.ts`
 * initialises it before any test file runs. `vi.resetModules()` does not undo that
 * — a re-import hands back the same, already-initialised instance, and a second
 * `.init()` on it does not re-run detection. A test that stubbed `navigator` and
 * re-imported this module therefore asserted nothing at all: it read the language
 * setup.ts had already resolved. Exporting the options is what lets the test use
 * `createInstance()` and still be testing the shipped configuration rather than a
 * copy of it that can silently drift.
 */
export const I18N_INIT_OPTIONS: InitOptions = {
  // No `resources` and no `partialBundledLanguages`: nothing is bundled, so the
  // backend is the only source and i18next's default behaviour is correct.
  //
  // fallbackLng: false is deliberate and load-bearing. 'en-US' made i18next
  // fetch the ENGLISH copy of every namespace alongside the active locale —
  // doubling the locale chunk count for anyone not on English — as insurance
  // against a missing key. That insurance is dead weight here:
  // i18n/__tests__/locale-coverage.test.ts fails the build if any of the eleven
  // locales lacks a single key, so English can never be needed at runtime.
  // Region tags still resolve, via convertDetectedLanguage below, not via a
  // fallback chain.
  fallbackLng: false,
  // The ELEVEN bundles, never CLIENT_LOCALES. This names what the lazy backend may fetch
  // JSON for; listing `de-AT` here would send i18next after a `locales/de-AT/` folder
  // that does not exist. Client region tags reach a bundle through `resolveLocale()`.
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: 'common',
  ns: [...NAMESPACES],
  interpolation: {
    escapeValue: false,  // React already escapes by default
  },
  detection: {
    // Order matters: check the lookup key in localStorage first (set
    // by the app after login when the user's preference is known),
    // then navigator, then HTML lang. Cookie excluded — we're SPA,
    // storage is the right place.
    order: ['localStorage', 'navigator', 'htmlTag'],
    lookupLocalStorage: 'studioflow.uiLocale',
    caches: ['localStorage'],
    // Normalise the detector's output to a BUNDLE tag before i18next ever tries
    // to load it. A phone reporting de-DE, de, en-GB, fr or pt must land on its
    // bundle. This used to be a post-init changeLanguage(), which meant the first
    // load asked for locales/de-DE/*.json (no such folder → empty resources) and
    // only then corrected itself — harmless while en-US was bundled and a flash
    // of raw keys now that nothing is. It is a DETECTOR option, not a core init
    // option. Pinned by __tests__/detectedLanguageResolution.test.ts.
    convertDetectedLanguage: (lng: string) => resolveLocale(lng),
  },
  // Explicit rather than implicit: this IS react-i18next's default, but with no
  // bundled resources it is now the mechanism that stops a component painting
  // raw keys while its namespace chunk is in flight. Every lazy route already
  // sits under a Suspense boundary, and main.tsx wraps the root in one for the
  // handful of components that render outside the router.
  react: { useSuspense: true },
  returnNull: false,
}

i18n
  .use(LazyBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({ ...I18N_INIT_OPTIONS })

syncHtmlLang(i18n)

export default i18n
